import "mapbox-gl/dist/mapbox-gl.css";
import Icon from "../../images/orderInformation/packageIcon.svg";
import classes from "./DriverLocation.module.css";
import ProgressFlow from "../UI/progressFlow/ProgressFlow";
import Map, { Marker } from "react-map-gl";
import { ArrowBtn } from "../buttons/arrowBtn/ArrowBtn";
import { CloseIcon } from "../../images/deliveries/deliveriesModals/xIcon";
import { StatusMessage } from "../statusMessage/StatusMessage";
import { useTranslations } from "../../helperFunctions/customHooks/useTranslations";
import { formatDateString } from "../../helperFunctions/formatDate";
import { useEscapeKeyClose } from "../../helperFunctions/customHooks/useEscapeKeyClose";
import { fetchDriverLocation } from "../../redux/driver/thunks/getDriverLocation";
import { useClickOutsideClose } from "../../helperFunctions/customHooks/useClickOutsideClose";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef, useState, useCallback } from "react";
import {
  clearPackageProgress,
  fetchPackageProgress,
} from "../../redux/package/getPackageProgress";
import {
  setIsDriverLocationFromRoutes,
  setIsDriverRoutes,
  setSelectedDriverLocationIndex,
  toggleDriverLocationModal,
} from "../../redux/globalFunctions/globalFnSlice";
import ModalWrapper from "../modalWrapper/ModalWrapper";

// Cache for geocoding results
const geocodeCache = {};

export function DriverLocation() {
  const {
    isDriverRoutes,
    isDriverLocationFromRoutes,
    selectedDriverLocationIndex,
    selectedDataForEdit,
  } = useSelector((state) => state.global);
  const currentDeliveriesState = useSelector(
    (state) => state.currentDeliveries
  );
  const deliveries = currentDeliveriesState.deliveries || [];
  const deliveriesState = useSelector((state) => state.currentDeliveries);
  const { status: historyStatus, deliveries: historyDeliveries } = useSelector(
    (state) => state.deliveryHistory
  );
  const { progress, status: progressStatus } = useSelector(
    (state) => state.getPackageProgress
  );

  const modalRef = useRef(null);
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const geocodingTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const { t } = useTranslations();

  const [coordinates, setCoordinates] = useState(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(selectedDriverLocationIndex);
  const [delivery, setDelivery] = useState([]);

  useEffect(() => {
    if (delivery?.p_id) {
      dispatch(fetchPackageProgress(delivery?.p_id));
    }
  }, [delivery?.p_id]);

  useEffect(() => {
    if (isDriverLocationFromRoutes && historyStatus === "succeeded") {
      setDelivery(historyDeliveries[currentIndex]);
    } else if (
      !isDriverLocationFromRoutes &&
      deliveriesState.status === "succeeded" &&
      deliveries.length !== 0
    ) {
      setDelivery(deliveries[currentIndex]);
    } else {
      setDelivery([]);
    }
  }, [
    isDriverLocationFromRoutes,
    currentIndex,
    deliveriesState.status,
    historyStatus,
  ]);

  const mapboxToken =
    "pk.eyJ1IjoiYm96aWRhcmszNSIsImEiOiJjbTFxNTdzMWUwOWZwMmpzYjN4eHF5Zmt5In0.fVeom9WzttFClBuhzNSjKg";

  function handleCloseModal() {
    dispatch(clearPackageProgress());
    dispatch(toggleDriverLocationModal());
    dispatch(setIsDriverRoutes(false));
    dispatch(setIsDriverLocationFromRoutes(false));
    dispatch(setSelectedDriverLocationIndex(0));
  }

  // Handle navigation between deliveries
  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (
      (!isDriverLocationFromRoutes && currentIndex < deliveries.length - 1) ||
      (isDriverLocationFromRoutes &&
        currentIndex < historyDeliveries.length - 1)
    ) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const isFirst = currentIndex === 0;
  const isLast = isDriverLocationFromRoutes
    ? currentIndex === historyDeliveries.length - 1
    : currentIndex === deliveries.length - 1;
  const hasOnlyOneDelivery = isDriverLocationFromRoutes
    ? historyDeliveries.length <= 0
    : deliveries.length <= 0;

  // Geocode a location and cache the result - memoized with useCallback
  const geocodeLocation = useCallback(
    async (location) => {
      if (!location) return null;

      // Check cache first
      if (geocodeCache[location]) {
        return geocodeCache[location];
      }

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            location
          )}.json?access_token=${mapboxToken}`
        );

        if (!response.ok) {
          throw new Error(`Geocoding API error: ${response.status}`);
        }

        const data = await response.json();

        if (data.features && data.features.length > 0) {
          const [longitude, latitude] = data.features[0].center;
          const result = { latitude, longitude };

          // Store in cache
          geocodeCache[location] = result;
          return result;
        } else {
          console.warn("No features found for location:", location);
        }
      } catch (error) {
        console.error("Error geocoding location:", location, error);
      }
      return null;
    },
    [mapboxToken]
  );

  // Fetch driver locations once on mount
  useEffect(() => {
    dispatch(fetchDriverLocation({}));

    // Cleanup timeout on unmount
    return () => {
      if (geocodingTimeoutRef.current) {
        clearTimeout(geocodingTimeoutRef.current);
      }
    };
  }, [dispatch]);

  // Function to update map location - extracted for reuse
  const updateMapLocation = useCallback(async () => {
    // If we've retried too many times, stop
    if (retryCountRef.current >= MAX_RETRIES) {
      console.warn("Max retries reached for geocoding");
      return;
    }

    let addressCoordinates = null;
    const address =
      delivery?.p_pickup_address_name || delivery?.p_pickup_address;

    if (address) {
      addressCoordinates = await geocodeLocation(address);
    }

    if (addressCoordinates) {
      setCoordinates(addressCoordinates);

      // If map is ready, fly to location
      if (mapRef.current && isMapInitialized) {
        try {
          mapRef.current.flyTo({
            center: [addressCoordinates.longitude, addressCoordinates.latitude],
            zoom: 14,
            duration: 1000,
          });
        } catch (error) {
          console.error("Error flying to location:", error);
        }
      }
    } else {
      // If we still don't have coordinates, retry after a delay (only if we have data to try)
      if (address) {
        retryCountRef.current += 1;

        // Exponential backoff for retries
        const delay = Math.min(
          1000 * Math.pow(2, retryCountRef.current),
          10000
        );
        geocodingTimeoutRef.current = setTimeout(updateMapLocation, delay);
      }
    }
  }, [isDriverRoutes, delivery, geocodeLocation, isMapInitialized]);

  // Update the map when delivery data changes
  useEffect(() => {
    // Only reset retry count and clear pending timeouts
    retryCountRef.current = 0;

    if (geocodingTimeoutRef.current) {
      clearTimeout(geocodingTimeoutRef.current);
    }

    // Update location if map is already initialized
    if (isMapInitialized) {
      updateMapLocation();
    }
  }, [delivery, isMapInitialized, updateMapLocation]);

  // Handle initial map load and location update
  const handleMapLoad = useCallback(() => {
    setIsMapInitialized(true);

    // If we already have coordinates when the map loads, fly to them
    if (coordinates && mapRef.current) {
      try {
        mapRef.current.flyTo({
          center: [coordinates.longitude, coordinates.latitude],
          zoom: 14,
          duration: 1000,
        });
      } catch (error) {
        console.error("Error flying to location on map load:", error);
      }
    } else {
      // First time loading - try to update location
      updateMapLocation();
    }
  }, [coordinates, updateMapLocation]);

  const isStatusLoading =
    deliveriesState.status === "loading" || historyStatus === "loading";

  const modalText = t.driverLocationsModal;

  // Initial view state is only set once when the map is first created
  const initialViewState = {
    latitude: 51.505,
    longitude: -0.09,
    zoom: 3,
  };

  return (
    <ModalWrapper
      bgColor={"#eef5ff"}
      onClick={handleCloseModal}
      closeButton={false}
    >
      <header className={classes.header}>
        <div className={classes.headerInfo}>
          <span>
            <img src={Icon} alt="Icon" />
          </span>
          <div
            className={classes.driverInfoContainer}
            style={{
              justifyContent: deliveries.length === 0 ? "center" : "",
            }}
          >
            <h2>
              {selectedDataForEdit?.u_firstname +
                " " +
                selectedDataForEdit?.u_lastname}
            </h2>
            {delivery.length !== 0 && (
              <h3>
                {modalText.order} {delivery?.p_id}
              </h3>
            )}
          </div>
        </div>
        <div className={classes.headerButtons}>
          <div className={classes.navButtons}>
            <ArrowBtn
              disabled={isFirst || hasOnlyOneDelivery}
              onClick={handlePrev}
              direction={"left"}
            />
            <ArrowBtn
              onClick={handleNext}
              disabled={isLast || hasOnlyOneDelivery}
              direction={"right"}
            />
          </div>
          <CloseIcon onClick={handleCloseModal} />
        </div>
      </header>

      <StatusMessage
        status={deliveriesState.status}
        text={{
          noItemsFound: modalText.noDeliveriesFound,
          errorFetching: modalText.noDeliveriesFound,
        }}
      />

      {!isStatusLoading && delivery.length === 0 && (
        <div className={classes.notificationsDiv}>
          {modalText.noDeliveriesFound}
        </div>
      )}

      {!isStatusLoading &&
        delivery &&
        delivery.length !== 0 &&
        (deliveriesState.status === "succeeded" ||
          historyStatus === "succeeded") && (
          <div className={classes.driverLocationContent}>
            <div className={classes.mapContainer}>
              <Map
                ref={mapRef}
                initialViewState={initialViewState}
                mapStyle="mapbox://styles/mapbox/streets-v11"
                mapboxAccessToken={mapboxToken}
                style={{ width: "100%", height: "100%" }}
                scrollZoom={false}
                dragRotate={false}
                attributionControl={false}
                onLoad={handleMapLoad}
                reuseMaps
              >
                {coordinates && (
                  <Marker
                    latitude={coordinates.latitude}
                    longitude={coordinates.longitude}
                    color="var(--secondaryColor)"
                  />
                )}
              </Map>
            </div>

            <div className={classes.rightSideContent}>
              <div className={classes.orderInformationContent}>
                <h6>{modalText.orderInformation}</h6>
                <div className={classes.orderInfo}>
                  <small>{modalText.date}</small>
                  <p>{formatDateString(delivery?.p_order_date)}</p>
                </div>
                <div className={classes.orderInfo}>
                  <small>{modalText.address}</small>
                  <p title={delivery?.p_dropoff_address_name}>
                    {delivery?.p_dropoff_address_name}
                  </p>
                </div>
              </div>
              {(progressStatus === "success" || progressStatus === "error") && (
                <ProgressFlow
                  progress={progress}
                  errorMsg={t.orderInformation.errorMsg}
                />
              )}
            </div>
          </div>
        )}
    </ModalWrapper>
  );
}
